<template>
  <div class="silo-page-blocks main-content wide">
    <div class="wrap">
      <h1>{{ pageData.pageTitle }}</h1>
      <div
        v-if="anchorList.length"
        class="anchor-list"
      >
        <a
          v-for="(anchor, index) in anchorList"
          :key="index"
          class="anchor"
          :href="anchor.anchor"
          @click="scrollToSection(anchor.anchor)"
        >
          {{ anchor.title }}
        </a>
      </div>

      <section
        v-for="(section, sectionIndex) in pageData.sections"
        :id="section.anchor"
        :key="sectionIndex"
        :class="{ divider: section.showDivider }"
      >
        <h2 v-if="section.title">{{ section.title }}</h2>

        <div
          v-for="(item, index) in section.large"
          :key="index"
          class="large-block"
        >
          <div
            class="img"
            :style="getBackgroundUrl(item)"
          />
          <div class="content">
            <h3>{{ item.title }}</h3>
            <div class="description">{{ item.description }}</div>
            <a
              v-if="item.link.text"
              :href="item.link.path"
            >
              {{ item.link.text }}
            </a>
          </div>
        </div>

        <div
          v-if="section.medium.length"
          class="blocks medium"
        >
          <div
            v-for="(item, index) in getRowsForSection(section.medium)"
            :key="index"
            class="medium-block"
            :class="{ compact: item.compact }"
          >
            <div
              class="img"
              :style="getBackgroundUrl(item)"
            />
            <div class="content">
              <h3 class="title">{{ item.title }}</h3>
              <div class="description">{{ item.description }}</div>
              <a
                v-if="item.link.text"
                class="action"
                :href="item.link.path"
              >
                {{ item.link.text }}
              </a>
            </div>
          </div>
        </div>

        <div
          v-if="section.small.length"
          class="blocks small"
        >
          <div
            v-for="(item, index) in getRowsForSection(section.small)"
            :key="index"
            class="small-block"
            :class="{ compact: item.compact }"
          >
            <div class="content">
              <h3 class="title">{{ item.title }}</h3>
              <div class="description">{{ item.description }}</div>
              <a
                v-if="item.link.text"
                class="action"
                :href="item.link.path"
              >
                {{ item.link.text }}
              </a>
            </div>
          </div>
        </div>
      </section>

      <div
        v-if="showBackToTop"
        class="back-to-top"
        @click="scrollToTop()"
      >
        {{ $t('footerWide.backToTop') }}
      </div>
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import ImageHelpers from 'utils/mixins/ImageHelpers'
  import ScrollUtils from 'utils/scrollUtils'

  export default {
    mixins: [ImageHelpers],
    computed: {
      ...mapState('siloBlockPage', ['pageData']),
      ...mapGetters('device', ['isDesktop']),

      showBackToTop() {
        let rows = 0
        this.pageData.sections.forEach((section) => {
          rows += section.large.length

          let mediumRows = Math.floor(section.medium.length / 3)
          let freeBlocksMedium = section.medium.length % 3
          rows += mediumRows + freeBlocksMedium

          let smallRows = Math.floor(section.small.length / 3)
          let freeBlocksSmall = section.small.length % 3
          rows += smallRows + freeBlocksSmall
        })

        return rows > 5
      },

      anchorList() {
        let anchors = []
        this.pageData.sections.forEach((section) => {
          if (section.anchor && section.title) {
            anchors.push({
              anchor: `#${section.anchor}`,
              title: section.title
            })
          }
        })
        return anchors
      }
    },
    mounted() {
      const anchor = window.location.hash
      if (anchor) {
        this.scrollToSection(anchor)
      }
    },
    methods: {
      getBackgroundUrl(block) {
        let img = this.$_ImageHelpers_responsiveImage({
          desktop: block.desktopImg,
          tablet: block.tabletImg,
          mobile: block.mobileImg
        })

        return this.$_ImageHelpers_getBackgroundImage(img.source)
      },
      scrollToSection(anchor) {
        let element = document.querySelector(anchor)
        if (!element) {
          return
        }
        let position = element.getBoundingClientRect().top

        if (this.isDesktop) {
          position = position - 130
        } else {
          position = position - 100
        }

        ScrollUtils.smoothScroll(position, 300)
      },
      scrollToTop() {
        ScrollUtils.smoothScroll(0, 300)
      },
      getRowsForSection(blocks) {
        let rowsCount = Math.floor(blocks.length / 3)
        const rowElements = rowsCount * 3

        if (rowsCount) {
          blocks.map((block, index) => {
            if (index < rowElements) {
              block.compact = true
            }
          })
        }

        return blocks
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';

  .silo-page-blocks {
    color: $blue-dark;
  }

  h1 {
    font-size: rem(32, 16);
    line-height: 48px;
    margin: 24px 0;
    font-weight: 500;

    @include bp-mf-tablet {
      font-size: rem(48, 16);
    }
  }

  h2 {
    margin: 0 0 24px;
    font-size: rem(28, 16);
    line-height: 32px;
    font-weight: 500;

    @include bp-mf-tablet {
      font-size: rem(32, 16);
      line-height: 48px;
    }
  }

  h3 {
    margin: 0 0 24px;
    font-size: rem(24, 16);
    font-weight: 500;
    line-height: 32px;
  }

  a {
    display: block;
    margin-top: 24px;
    color: $dark-blue;
    font-size: rem(16, 16);
    font-weight: 500;
  }

  .img {
    display: block;
    height: 198px;
    width: 100%;
    background-position: center center;
    background-size: cover;
  }

  section {
    @include bp-mf-tablet {
      scroll-margin-top: 100px;
    }

    @include bp-mf-desktop {
      scroll-margin-top: 130px;
    }
  }

  .wrap {
    padding: 0 20px;

    @include bp-mf-tablet {
      padding: 0 24px;
    }

    @include bp-mf-desktop {
      padding: 0;
    }
  }

  .anchor-list {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 40px;

    @include bp-mf-tablet {
      margin-bottom: 48px;
    }

    .anchor {
      padding: 16px 24px;
      margin-right: 14px;
      color: $blue-dark-ui;
      font-weight: 500;
      line-height: 16px;
      border-radius: 4px;
      background-color: rgba($blue-dark-ui, 0.05);
      text-decoration: none;

      @include bp-mf-tablet {
        padding: 16px 32px;
        margin-right: 24px;
        font-size: em(16);
      }
    }
  }

  .divider {
    &::after {
      content: '';
      display: block;
      margin: 38px auto;
      height: 3px;
      width: 33%;
      background: $green-base;
      background: -webkit-gradient(
        radial,
        50% 50%,
        0,
        50% 50%,
        50,
        from($green-base),
        to(white)
      );

      @include bp-mf-tablet {
        margin: 78px auto;
        background: -webkit-gradient(
          radial,
          50% 50%,
          0,
          50% 50%,
          150,
          from($green-base),
          to(white)
        );
      }
    }
  }

  .blocks {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .content {
    padding: 24px 16px 40px;
    font-size: rem(16, 16);
    line-height: 28px;
  }

  .large-block {
    margin-bottom: 40px;
    box-shadow: 0 1px 4px 0 rgba(192, 200, 216, 0.3);
    border-radius: 8px;

    @include bp-mf-tablet {
      display: flex;
      margin-bottom: 80px;
      border-radius: 0;
      box-shadow: none;

      .content {
        display: flex;
        width: 350px;
        flex-direction: column;
        padding: 0;
        margin-left: 24px;
        margin-right: 0;
      }

      .img {
        height: 384px;
        width: 682px;
      }

      &:nth-of-type(2) {
        .img {
          order: 2;
        }

        .content {
          margin-left: 0;
          margin-right: 24px;
        }
      }
    }

    @include bp-mf-desktop {
      .content {
        margin-left: 80px;
      }

      &:nth-of-type(2) {
        .content {
          margin-right: 80px;
        }
      }
    }
  }

  .medium-block {
    box-shadow: 0 1px 4px 0 rgba($scn-gray-border, 0.5);
    border-radius: 8px;
    margin-bottom: 50px;
    overflow: hidden;

    @include bp-mf-tablet {
      display: flex;
      width: 100%;

      &.compact {
        width: calc(33% - 20px);
        max-width: 340px;
        flex-direction: column;
      }

      .img {
        height: 340px;
        max-width: 340px;
        flex-shrink: 0;
      }
    }
  }

  .small-block {
    width: 100%;
    margin-bottom: 40px;
    border-radius: 8px;
    background-color: $white-dropdown-header;

    @include bp-mf-tablet {
      .content {
        padding: 24px 24px 40px;
      }

      &.compact {
        width: calc(33% - 20px);
      }
    }
  }

  .small-block, .medium-block {
    .content {
      padding: rem(24, 16) rem(16, 16) rem(40, 16) rem(16, 16);
      display: flex;
      flex-flow: column;
      justify-content: space-between;

      .description {
        flex-grow: 1;
      }

      .title, .action {
        flex-grow: 0;
      }

      @include bp-mf-tablet {
        height: 100%;
        padding-left: rem(24, 16);
        padding-right: rem(24, 16);
      }
    }
  }

  .back-to-top {
    margin: 40px 0 70px;
    font-size: rem(16, 16);
    line-height: 16px;
    text-align: center;
    color: $blue-topaz;
    cursor: pointer;

    &::after {
      display: block;
      content: $icon-arrow-right-thin;
      @include icon-font-thin();
      margin-left: 16px;
      width: 48px;
      height: 48px;
      line-height: 48px;
      right: 35px;
      transform: rotate(-90deg);
      text-align: center;
      font-weight: 700;
      color: $blue-medium;
      border-radius: 50%;
      background-color: $blue-bg-css;
    }

    @include bp-mf-tablet {
      text-align: right;
    }
  }
</style>
